import {
  PORTAL_IMAGE_OUTPUTS,
  getPortalImageSizePolicy,
  getPortalImageSurface,
} from "./portalImageContract";
import { normalizePublicMediaUrl } from "./publicMediaUrl";

export const PORTAL_IMAGE_FORMATS = Object.freeze(["avif", "webp", "jpg", "jpeg", "png"]);

const DESTINATION_MEDIA_ROLE_BY_SLOT = Object.freeze({
  gallery_grid_main: "desktop",
  gallery_grid_side: "desktop",
  gallery_tile_4x3: "desktop",
  gallery_tile_landscape: "desktop",
  gallery_story_mobile: "mobile",
  gallery_story_tablet: "tablet",
  gallery_lightbox: "desktop",
  gallery_thumb: "desktop",
  gallery_search_thumb: "desktop",
});

const asPositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const materializedVariant = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  let pathname = raw;
  try {
    pathname = new URL(raw, "https://contract.invalid").pathname;
  } catch (_error) {
    pathname = raw.split(/[?#]/)[0];
  }
  const match = pathname.match(/\/media\/([^/]+)\/v([^/]+)\/([^/]+)\/([^/.]+)\.([a-z0-9]+)$/i);
  return match ? {
    imageCode: match[1],
    version: match[2],
    role: match[3],
    variantKey: match[4],
    format: match[5].toLowerCase(),
  } : null;
};

export const normalizeDeclaredPortalVariant = (variant = {}, delivery = {}) => {
  const rawUrl = String(variant.url || variant.path || "").trim();
  const baseUrl = String(delivery.baseUrl || "").trim().replace(/\/+$/, "");
  const url = !rawUrl
    ? ""
    : /^https?:\/\//i.test(rawUrl) || rawUrl.startsWith("data:")
      ? rawUrl
      : `${baseUrl}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;
  return {
    slotKey: String(variant.slotKey || "").trim(),
    role: String(variant.role || "default").trim().toLowerCase(),
    variantKey: String(variant.variantKey || variant.variant || "").trim(),
    format: String(variant.format || "webp").trim().toLowerCase(),
    url: normalizePublicMediaUrl(url),
    width: asPositiveNumber(variant.width),
    height: asPositiveNumber(variant.height),
    bytes: asPositiveNumber(variant.bytes || variant.size || variant.contentLength),
  };
};

export const validateDeclaredPortalVariant = ({ variant, delivery, surfaceKey, slotKey }) => {
  const candidate = normalizeDeclaredPortalVariant(variant, delivery);
  const expected = PORTAL_IMAGE_OUTPUTS[slotKey] || null;
  const policy = getPortalImageSizePolicy(surfaceKey);
  const surface = getPortalImageSurface(surfaceKey);
  const reasons = [];
  const warnings = [];

  if (!candidate.url) reasons.push("url_missing");
  const declaredPreset = String(delivery?.presetKey || delivery?.preset || "").trim();
  const acceptedPresetKeys = new Set([surface?.presetKey, ...(surface?.compatiblePresetKeys || [])].filter(Boolean));
  if (declaredPreset && acceptedPresetKeys.size && !acceptedPresetKeys.has(declaredPreset)) {
    reasons.push("preset_mismatch");
  }
  if (!candidate.slotKey) reasons.push("slot_missing");
  else if (candidate.slotKey !== slotKey) reasons.push("slot_mismatch");
  if (!candidate.variantKey) reasons.push("variant_key_missing");
  else if (expected?.variantKey && candidate.variantKey !== expected.variantKey) {
    reasons.push("variant_key_mismatch");
  }
  if (!PORTAL_IMAGE_FORMATS.includes(candidate.format)) reasons.push("format_unsupported");
  if (expected?.width && candidate.width && candidate.width !== expected.width) reasons.push("width_mismatch");
  if (expected?.height && candidate.height && candidate.height !== expected.height) reasons.push("height_mismatch");
  if (policy?.maxWidth && candidate.width && candidate.width > policy.maxWidth) reasons.push("width_budget_exceeded");
  if (policy?.maxBytes && candidate.bytes && candidate.bytes > policy.maxBytes) reasons.push("byte_budget_exceeded");
  const destinationMediaRole = ["destination_media", "inventory_media"].includes(declaredPreset)
    ? DESTINATION_MEDIA_ROLE_BY_SLOT[slotKey]
    : "";
  const acceptedRoles = new Set([expected?.role, destinationMediaRole].filter(Boolean));
  if (acceptedRoles.size && !acceptedRoles.has(candidate.role)) warnings.push("role_mismatch");
  if (!candidate.width || (expected?.height && !candidate.height)) warnings.push("dimensions_unreported");
  if (!candidate.bytes) warnings.push("bytes_unreported");

  return Object.freeze({
    valid: reasons.length === 0,
    candidate: Object.freeze(candidate),
    expected,
    policy,
    reasons: Object.freeze(reasons),
    warnings: Object.freeze(warnings),
  });
};

export const validatePortalImageFallback = (
  url,
  { slotKey, allowLegacy = true, allowMaterializedAsDeclared = false } = {},
) => {
  const normalizedUrl = String(url || "").trim();
  if (!normalizedUrl) return { valid: false, reason: "fallback_missing", materialized: null };
  const parsed = materializedVariant(normalizedUrl);
  if (!parsed) {
    return {
      valid: Boolean(allowLegacy),
      reason: allowLegacy ? "explicit_legacy" : "optimized_declaration_required",
      materialized: null,
    };
  }
  if (allowMaterializedAsDeclared) {
    return { valid: true, reason: "explicit_materialized", materialized: parsed };
  }
  const expected = PORTAL_IMAGE_OUTPUTS[slotKey];
  if (!expected || parsed.variantKey !== expected.variantKey) {
    return { valid: false, reason: "fallback_variant_mismatch", materialized: parsed };
  }
  return { valid: true, reason: "declared_materialized", materialized: parsed };
};

export const auditPortalDeliveryForSurface = (delivery, { surfaceKey, slotKey }) => {
  const declarations = Array.isArray(delivery?.variants) ? delivery.variants : [];
  const evaluated = declarations
    .filter((variant) => String(variant?.slotKey || "") === slotKey)
    .map((variant) => validateDeclaredPortalVariant({ variant, delivery, surfaceKey, slotKey }));
  return Object.freeze({
    status: evaluated.some((entry) => entry.valid) ? "valid" : "missing-variant",
    valid: Object.freeze(evaluated.filter((entry) => entry.valid).map((entry) => entry.candidate)),
    rejected: Object.freeze(evaluated.filter((entry) => !entry.valid).map((entry) => Object.freeze({
      candidate: entry.candidate,
      reasons: entry.reasons,
    }))),
    declarationCount: evaluated.length,
  });
};

export default auditPortalDeliveryForSurface;
